import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./ImageEditPage.css";

const baseUrl = process.env.PUBLIC_URL || "";

async function api(path, options = {}) {
  const res = await fetch(path, {
    credentials: "include",
    ...options,
  });
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }
  if (!res.ok) {
    const message = (data && data.error) || `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data;
}

function ImageEditPage() {
  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [filenames, setFilenames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  const refreshFolders = useCallback(async () => {
    const data = await api("/api/images");
    const list = Array.isArray(data.folders) ? data.folders : [];
    setFolders(list);
    setFolder((current) => current || list[0] || "");
  }, []);

  const refreshImages = useCallback(async (selectedFolder) => {
    if (!selectedFolder) {
      setFilenames([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const files = await api(`/api/images/${encodeURIComponent(selectedFolder)}`);
      setFilenames(Array.isArray(files) ? files : []);
    } catch (err) {
      setError(err.message);
      setFilenames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api("/api/auth/me");
        if (cancelled) return;
        setAuthenticated(Boolean(me.authenticated));
        if (me.authenticated) {
          await refreshFolders();
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshFolders]);

  useEffect(() => {
    if (authenticated && folder) {
      refreshImages(folder);
    }
  }, [authenticated, folder, refreshImages]);

  async function handleLogin(event) {
    event.preventDefault();
    setError("");
    setStatus("");
    try {
      await api("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      setAuthenticated(true);
      setPassword("");
      await refreshFolders();
      setStatus("已登录");
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleLogout() {
    setError("");
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch {
      // still clear local UI state
    }
    setAuthenticated(false);
    setFolders([]);
    setFolder("");
    setFilenames([]);
    setStatus("已退出");
  }

  async function handleUpload(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !folder) return;

    setUploading(true);
    setError("");
    setStatus("");
    try {
      const body = new FormData();
      files.forEach((file) => body.append("files", file));
      const result = await api(`/api/images/${encodeURIComponent(folder)}`, {
        method: "POST",
        body,
      });
      const count = (result.uploaded || []).length;
      setStatus(`已上传 ${count} 张`);
      await refreshImages(folder);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(filename) {
    if (!folder) return;
    if (!window.confirm(`确定删除 ${filename}？`)) return;

    setError("");
    setStatus("");
    try {
      await api(
        `/api/images/${encodeURIComponent(folder)}/${encodeURIComponent(filename)}`,
        { method: "DELETE" }
      );
      setStatus(`已删除 ${filename}`);
      await refreshImages(folder);
    } catch (err) {
      setError(err.message);
    }
  }

  if (!authChecked) {
    return (
      <div className="image-edit-page">
        <p className="image-edit-status">加载中…</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="image-edit-page">
        <div className="image-edit-login">
          <h1>图片管理</h1>
          <p>请输入编辑密码</p>
          <form onSubmit={handleLogin}>
            <label>
              密码
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button type="submit" className="primary">登录</button>
          </form>
          {error ? <p className="image-edit-error">{error}</p> : null}
          {status ? <p className="image-edit-status">{status}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="image-edit-page">
      <div className="image-edit-header">
        <div>
          <h1>图片管理</h1>
          <p>预览、上传、删除各相册图片</p>
        </div>
        <div className="image-edit-actions">
          <Link to="/edit/preview/home">返回预览</Link>
          <button type="button" onClick={handleLogout}>退出</button>
        </div>
      </div>

      <div className="image-edit-folders">
        {folders.map((name) => (
          <button
            key={name}
            type="button"
            className={name === folder ? "active" : ""}
            onClick={() => setFolder(name)}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="image-edit-toolbar">
        <div>
          当前文件夹：<strong>{folder || "—"}</strong>
          {loading ? "（加载中…）" : `（${filenames.length} 张）`}
        </div>
        <label className={`image-edit-upload${uploading || !folder ? " disabled" : ""}`}>
          {uploading ? "上传中…" : "上传图片"}
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.gif,image/*"
            multiple
            disabled={uploading || !folder}
            onChange={handleUpload}
          />
        </label>
      </div>

      {error ? <p className="image-edit-error">{error}</p> : null}
      {status ? <p className="image-edit-status">{status}</p> : null}

      <div className="image-edit-grid">
        {filenames.map((filename) => (
          <div className="image-edit-item" key={filename}>
            <img
              src={`${baseUrl}/${folder}/${encodeURIComponent(filename)}`}
              alt={filename}
            />
            <div className="name">{filename}</div>
            <button type="button" className="danger" onClick={() => handleDelete(filename)}>
              删除
            </button>
          </div>
        ))}
      </div>

      {!loading && filenames.length === 0 ? (
        <p className="image-edit-status">此文件夹暂无图片</p>
      ) : null}
    </div>
  );
}

export default ImageEditPage;
